import { useEffect, useState } from 'react';
import { AlertTriangle, Check, ExternalLink, GitBranch, Link2, Plus, X } from 'lucide-react';
import type { MatchCandidate } from '@/services/ingestion';

/**
 * Match preview — the pre-commit duplicate decision surface (register A8).
 *
 * Before an intake record is committed into the pipeline, the engine surfaces
 * existing deals it may be a re-upload of: an exact Antarctica Control ID match,
 * or a fuzzy match on normalized address / sponsor. Nothing is merged
 * automatically — the operator decides here: attach this record to an existing
 * deal, or create a new one.
 */

const BASIS_LABEL: Record<string, string> = {
  control_id_exact: 'Control ID · exact',
  property_name_exact: 'Property name · exact',
  address_exact: 'Street address · exact',
  address_fuzzy: 'Address · fuzzy',
  sponsor_fuzzy: 'Sponsor · fuzzy',
};

function amountText(value: number | null | undefined): string | null {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return null;
  return `$${(value / 1_000_000).toLocaleString('en-US', { maximumFractionDigits: 2 })}M`;
}

export default function MatchPreviewDialog({
  candidates,
  note,
  busy,
  onAttach,
  onCreateNew,
  onClose,
  onOpenDeal,
}: {
  candidates: MatchCandidate[];
  note?: string;
  busy: boolean;
  onAttach: (loanId: string) => void;
  onCreateNew: () => void;
  onClose: () => void;
  /** Open the matched deal's file in place, so the choice is not made blind
   *  (register P13: every match statement carries a click-through). */
  onOpenDeal?: (loanId: string) => void;
}) {
  const [selected, setSelected] = useState<string>(candidates[0]?.loanId ?? '');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && !busy && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, busy]);

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
      onClick={() => !busy && onClose()}
    >
      <div
        className="w-full max-w-lg rounded-lg border border-amber-500/20 bg-stone-950 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-stone-800 px-4 py-3">
          <div className="flex items-center gap-2">
            <GitBranch size={14} className="text-amber-400" />
            <span className="text-[12px] font-medium text-stone-200">
              Possible duplicate — attach or create new
            </span>
          </div>
          <button
            onClick={() => !busy && onClose()}
            className="text-stone-500 hover:text-stone-300"
            title="Close"
          >
            <X size={15} />
          </button>
        </div>

        <div className="space-y-4 px-4 py-4">
          <p className="text-[11px] text-stone-400">
            {note ??
              'This record may be a re-upload of an existing deal. Nothing is merged automatically — attach it to one of the deals below, or create a new deal.'}
          </p>

          <div className="space-y-1.5">
            <span className="block text-[10px] uppercase tracking-wide text-stone-500">
              Existing deals it may match
            </span>
            {candidates.map((c) => {
              const active = selected === c.loanId;
              const name = c.property || 'Unnamed deal';
              const amount = amountText(c.loanAmount);
              return (
                <div
                  key={c.loanId}
                  className={`flex w-full items-center gap-2 rounded border px-3 py-2 transition ${
                    active
                      ? 'border-amber-400/60 bg-amber-400/10'
                      : 'border-stone-700 bg-stone-900 hover:border-stone-600'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setSelected(c.loanId)}
                    disabled={busy}
                    aria-pressed={active}
                    className="flex min-w-0 flex-1 items-center gap-2 text-left disabled:opacity-50"
                  >
                    <span
                      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
                        active ? 'border-amber-400 bg-amber-400/20' : 'border-stone-600'
                      }`}
                    >
                      {active ? <Check size={10} className="text-amber-300" /> : null}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[12px] font-medium text-stone-200">
                        {name}
                      </span>
                      <span className="block truncate text-[10px] text-stone-400">
                        {[c.location, amount].filter(Boolean).join(' · ') || 'No address or amount on the deal'}
                      </span>
                      <span className="block truncate font-mono text-[10px] text-stone-500">
                        {c.loanId}
                        {c.controlId ? ` · ${c.controlId}` : ''}
                      </span>
                    </span>
                    <span className="shrink-0 text-right">
                      <span className="block text-[10px] text-amber-300/80">
                        {BASIS_LABEL[c.basis] ?? c.basis}
                      </span>
                      <span className="block text-[10px] text-stone-500">
                        {Math.round((c.score ?? 0) * 100)}% match
                      </span>
                    </span>
                  </button>
                  {onOpenDeal ? (
                    <button
                      type="button"
                      onClick={() => onOpenDeal(c.loanId)}
                      aria-label={`Open deal ${name}`}
                      title={`Open ${name} (${c.loanId}) to compare before deciding`}
                      className="shrink-0 rounded border border-stone-700 p-1 text-stone-400 hover:border-amber-400/40 hover:text-amber-300"
                    >
                      <ExternalLink size={12} />
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-stone-800 px-4 py-3">
          <button
            onClick={onCreateNew}
            disabled={busy}
            className="btn-secondary flex items-center gap-1.5 px-3 py-1.5 text-[11px] disabled:opacity-50"
          >
            <Plus size={12} />
            Create new deal
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={() => !busy && onClose()}
              disabled={busy}
              className="px-3 py-1.5 text-[11px] font-medium text-stone-400 hover:text-stone-200 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={() => selected && onAttach(selected)}
              disabled={busy || !selected}
              className="btn-primary flex items-center gap-1.5 px-3 py-1.5 text-[11px] disabled:opacity-50"
            >
              <Link2 size={12} />
              {busy ? 'Working…' : 'Attach to selected'}
            </button>
          </div>
        </div>

        {candidates.length === 0 && (
          <div className="flex items-center gap-2 border-t border-stone-800 px-4 py-3 text-[11px] text-stone-500">
            <AlertTriangle size={13} /> No likely existing deal matched.
          </div>
        )}
      </div>
    </div>
  );
}
